import { KeepMountedPanel, keepMountedPanelId, Panel, PanelGroup, PanelHandle } from "@qw/ui";
import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { ChatPosition } from "@/providers/page-provider";
import { usePage } from "@/providers/use-page";
import { ChatColumn } from "./chat-column";

interface ResizableChatLayoutProps {
  autoSaveId: string;
  children: ReactNode;
}

const CHAT_MIN_WIDTH_PX = 320;
const CONTENT_MIN_SIZE_PERCENT = 42;
const CHAT_MAX_MIN_SIZE_PERCENT = 100 - CONTENT_MIN_SIZE_PERCENT;

export const ResizableChatLayout = ({ autoSaveId, children }: ResizableChatLayoutProps) => {
  const layoutRef = useRef<HTMLDivElement | null>(null);
  const { chatPosition, isChatOpen } = usePage();
  const isSideChatVisible = chatPosition === ChatPosition.side && isChatOpen;
  const chatPanelId = keepMountedPanelId("chat", !isSideChatVisible);
  const panelIds = useMemo(() => ["content", chatPanelId], [chatPanelId]);
  const [chatMinSize, setChatMinSize] = useState("18%");
  const defaultLayout = useMemo<Record<string, number>>(() => {
    const layout: Record<string, number> = {
      content: isSideChatVisible ? 74 : 100,
    };

    layout[chatPanelId] = isSideChatVisible ? 26 : 0;

    return layout;
  }, [chatPanelId, isSideChatVisible]);

  useEffect(() => {
    if (!isSideChatVisible) {
      setChatMinSize("0%");
      return;
    }

    const updateChatMinSize = () => {
      const width = layoutRef.current?.getBoundingClientRect().width;

      if (!width) {
        return;
      }

      const minSizePercent = Math.min(CHAT_MAX_MIN_SIZE_PERCENT, (CHAT_MIN_WIDTH_PX / width) * 100);
      setChatMinSize(`${minSizePercent.toFixed(3)}%`);
    };

    updateChatMinSize();

    const layoutElement = layoutRef.current;

    if (!layoutElement || typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateChatMinSize);
      return () => window.removeEventListener("resize", updateChatMinSize);
    }

    const resizeObserver = new ResizeObserver(updateChatMinSize);
    resizeObserver.observe(layoutElement);

    return () => resizeObserver.disconnect();
  }, [isSideChatVisible]);

  return (
    <div ref={layoutRef} className="flex min-h-0 min-w-0 flex-auto">
      <PanelGroup
        direction="horizontal"
        autoSaveId={autoSaveId}
        defaultLayout={defaultLayout}
        panelIds={panelIds}
        className="min-h-0 min-w-0 flex-auto"
      >
        <Panel
          id="content"
          minSize={isSideChatVisible ? `${CONTENT_MIN_SIZE_PERCENT}%` : "100%"}
          defaultSize="74%"
          className="flex min-h-0 min-w-0 overflow-hidden"
        >
          {children}
        </Panel>
        <PanelHandle hidden={!isSideChatVisible} />
        <KeepMountedPanel
          id="chat"
          hidden={!isSideChatVisible}
          minSize={chatMinSize}
          defaultSize="26%"
          className="min-h-0 min-w-0 overflow-hidden"
        >
          <ChatColumn layout={isSideChatVisible ? "panel" : "flex"} mountOnly={!isChatOpen} />
        </KeepMountedPanel>
      </PanelGroup>
    </div>
  );
};
